import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, OverlayTrigger, Tooltip, Spinner, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../Auth/AuthContext';
import { useData } from '../Data/DataContext';
import './Dashboard.css';
import './colors.css';
import CustomContainer from "../Utils/CustomContainer";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationTriangle, 
  faUserInjured, 
  faUserNurse, 
  faPencilAlt,
  faInfoCircle,
  faUserFriends
} from '@fortawesome/free-solid-svg-icons';
import ClientDetailModal from '../ClientDetail/ClientDetailModal';
import sickLeaveWarnings from "./sickLeaveWarnings";
import {IClient} from "../../utils/FrontendDataTypes";
import {
  employeeNeedsToChooseStandIn,
  employeeNeedsToChooseStandInForToday
} from "../../utils/utils";
import {format} from "date-fns";
import {de} from "date-fns/locale";

// Typen für die Menüoptionen
interface MenuOption {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  disabledReason?: string;
  action: () => void;
  icon?: any; // Icon für die Menüoption
  color?: string; // Primärfarbe für die Option
  urgency?: 'low' | 'medium' | 'high'; // Dringlichkeitsstufe
  buttonText: string; // Text für den Button
  urgentButtonText?: string; // Text für den Button bei hoher Dringlichkeit
}

const Dashboard: React.FC = () => {
  const { resetAuth } = useAuth();
  const { data, loading, error, refreshData } = useData();
  const navigate = useNavigate();
  const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]); 
  const [showClientInfoModal, setShowClientInfoModal] = useState<boolean>(false);
  const [currentClient, setCurrentClient] = useState<IClient | null>(null);

  // Status für die Verfügbarkeit der verschiedenen Optionen und Warnhinweise
  const [incompleteSickLeaveReportsCount, setIncompleteSickLeaveReportsCount] = useState<number>(0);

// Aktualisieren der Status-Variablen und Menüoptionen basierend auf den geladenen Daten
useEffect(() => {
  if (!data) return;

  // Farbpalette für Menüoptionen
  const baseColor = '#4285f4'; // Standard-Blau für normale Optionen
  const warningColor = '#f39c12'; // Orange für unvollständige Krankmeldungen
  const dangerColor = '#e74c3c'; // Sanfteres Rot für ClientStandIn-Benachrichtigungen

  const incompleteClientSickLeave = data.client_sick_leave.filter((c) => {
    return !c.to
  })
  const incompleteEmployeeSickLeave = data.employee_sick_leave.filter((esl) => {
    return !esl.employee_submitted_data
  })
  setIncompleteSickLeaveReportsCount(incompleteEmployeeSickLeave.length + incompleteClientSickLeave.length);

  // Menüoptionen direkt hier erstellen und setzen
  const standInUrgency = (() : 'low' | 'medium' | 'high' => {
    if (!data.employee_sick_today && data.is_employee_client_sick_today && !data.stand_in_for_client_today) {
      return 'high';
    }
    return 'low';
  })()
  const options: MenuOption[] = [
    {
      id: 'submit-sick-leave-report',
      title: 'Krankmeldung einreichen',
      description: 'Melden Sie sich selbst, Ihr Kind oder Ihren Klienten krank.',
      enabled: true, // Diese Option ist immer verfügbar
      action: () => navigate('/sick-report/new'),
      icon: faUserInjured,
      color: baseColor,
      urgency: 'low', // Normale Priorität
      buttonText: 'Krankmeldung erstellen'
    },
    {
      id: 'clientStandIn',
      title: 'Vertretung auswählen',
      description: 'Wählen Sie einen Klienten aus, den Sie vertreten möchten.',
      enabled: employeeNeedsToChooseStandIn(data),
      disabledReason: (() : string => {
        if (data.is_employee_client_sick_today && data.is_employee_client_sick_next_work_day) {
          return 'Sie können keinen Klienten auswählen, da Sie krank sind.';
        }
        return 'Sie können kein Vertretung wählen.'
      })(),
      action: () => handleNavigate('/standin'),
      icon: faUserNurse,
      color: (() : string => {
        if (employeeNeedsToChooseStandInForToday(data)) {
          return dangerColor;
        }
        return baseColor;
      })(),
      urgency: standInUrgency,
      buttonText: 'Vertretung suchen',
      urgentButtonText: 'Vertretung dringend wählen'
    }
  ];

  setMenuOptions(options);

}, [data]);

  // Funktion zum Navigieren zu anderen Seiten
  const handleNavigate = (path: string) => {
    console.log(`Navigation zu: ${path}`);
    // React Router Navigation verwenden
    navigate(path);
  };

  // Funktion zum Anzeigen der Klientendaten
  const handleShowClientInfo = () => {
    // Entweder den zugewiesenen Klienten oder den Vertretungsklienten anzeigen
    const clientToShow = data?.stand_in_for_client_today ||
      data?.clients.find(client => client.accompanied_by_employee === data.employee_id) || 
      null;

    if (clientToShow) {
      setCurrentClient(clientToShow);
      setShowClientInfoModal(true);
    }
  };


  const warnings = []

  if (data && employeeNeedsToChooseStandInForToday(data)) {
    warnings.push(<Alert key={"warning_client_stand_in"} variant="danger" className="dashboard-alert mb-3" style={{ backgroundColor: 'rgba(231, 76, 60, 0.1)', borderColor: '#e74c3c', color: '#c0392b' }}>
      <Row className="align-items-center">
        <Col lg={8} md={7} xs={12}>
          <h4 className="alert-heading">
            Vertretung auswählen
            <Badge bg="danger" style={{ backgroundColor: '#e74c3c' }} className="ms-2 py-1">Dringend</Badge>
          </h4>
          <p className="mb-0">
            Ihr Klient ist krank. Bitte einen Klienten zur Vertretung auswählen.
          </p>
        </Col>
        <Col lg={4} md={5} xs={12} className="mt-md-0 mt-3 text-md-end">
          <Button
              variant="danger"
              className="alert-button"
              style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c' }}
              onClick={() => handleNavigate('/standin')}
          >
            Jetzt Klienten wählen
          </Button>
        </Col>
      </Row>
    </Alert>);
  }

  warnings.push(... sickLeaveWarnings(data?.employee_sick_leave ?? [], data?.client_sick_leave ?? [], data?.clients ?? []).map((warning) => {
    return <Alert key={warning.key} variant={warning.variant} className="dashboard-alert">
      <Row className="align-items-center">
        <Col lg={8} md={7} xs={12}>
          <h4 className="alert-heading">
            {warning.header}
          </h4>
          <p className="mb-0">
            {warning.text}
          </p>
        </Col>
        <Col lg={4} md={5} xs={12} className="mt-md-0 mt-3 text-md-end">
          <Button
              variant={warning.variant}
              className="alert-button"
              onClick={() => handleNavigate(warning.path)}
          >
            {warning.buttonText}
          </Button>
        </Col>
      </Row>
    </Alert>
  }))

  return (
    <CustomContainer>
      <Row className="mb-4">
        <Col>
          <h1 className="dashboard-title">Willkommen im Mitarbeiterportal</h1>
          <p className="dashboard-subtitle">Hier finden Sie Ihre Übersicht und Aufgaben</p>
        </Col>
      </Row>

      {loading && (
        <Row className="mb-4">
          <Col className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Lade Daten...</span>
            </Spinner>
            <p className="mt-2">Daten werden geladen...</p>
          </Col>
        </Row>
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              {error}
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-3"
                onClick={refreshData}
              >
                Erneut versuchen
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {!loading && !error && data && (
        <>
          {/* Klienten-Informationen */}
          <Row className="mb-4">
            <Col>
              <Card className="border-info shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 className="mb-0">
                        <FontAwesomeIcon icon={faUserFriends} className="me-2 text-info" />
                        {data.stand_in_for_client_today ? 'Heute vertretener Klient:' : 'Ihr zugewiesener Klient:'}
                      </h4>
                      <p className="mb-0 mt-2">
                        {data.stand_in_for_client_today ?
                          `${data.stand_in_for_client_today.first_name} ${data.stand_in_for_client_today.last_name}` :
                          data.clients.find(client => client.accompanied_by_employee === data.employee_id) ? 
                            `${data.clients.find(client => client.accompanied_by_employee === data.employee_id)?.first_name} ${data.clients.find(client => client.accompanied_by_employee === data.employee_id)?.last_name}` : 
                            'Kein Klient zugewiesen'}
                      </p>
                    </div>
                    <Button
                      variant="outline-info"
                      onClick={handleShowClientInfo}
                      disabled={!data.stand_in_for_client_today && !data.clients.find(client => client.accompanied_by_employee === data.employee_id)}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                      Klientendaten anzeigen
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {data.stand_in_for_client_next_work_day && (
              <Row className="mb-4">
                <Col>
                  <Card className="border-info shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h4 className="mb-0">
                            <FontAwesomeIcon icon={faUserFriends} className="me-2 text-info" />
                            Am {format(data.next_work_day, "EEE d. MMMM", {locale: de})} vertretener Klient
                          </h4>
                          <p className="mb-0 mt-2">
                            {data.stand_in_for_client_next_work_day.first_name} {data.stand_in_for_client_next_work_day.last_name}
                          </p>
                        </div>
                        <Button
                            variant="outline-info"
                            onClick={() => {
                              setCurrentClient(data?.stand_in_for_client_next_work_day ?? null);
                              setShowClientInfoModal(true);
                            }}
                        >
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                          Klientendaten anzeigen
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
          )}

          {/* Warnhinweise und Aufgaben */}
          {(warnings.length > 0) && (
            <Row className="mb-4">
              <Col>
                <div className="alert-section">
                  <h2 className="section-title">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Wichtige Hinweise
                  </h2>
                  {warnings}
                </div>
              </Col>
            </Row>
          )}

          {/* Hauptmenü-Optionen */}
          <Row className="mb-4">
            <Col>
              <h2 className="section-title">Verfügbare Aktionen</h2>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            {menuOptions.map((option) => (
              <Col xs={12} key={option.id}>
                <div 
                  className={`menu-item ${!option.enabled ? 'disabled-item' : ''} ${option.urgency === 'high' ? 'urgent-item' : ''} ${option.urgency === 'medium' ? 'medium-urgent-item' : ''}`}
                  style={option.enabled ? { 
                    borderColor: option.urgency !== 'low' ? option.color : 'transparent', 
                    background: `linear-gradient(135deg, white 85%, ${option.color}40 100%)` 
                  } : {}}
                >
                  {option.urgency === 'high' && (
                    <Badge bg="danger" style={{ backgroundColor: '#e74c3c' }} className="position-absolute top-0 end-0 m-2">
                      Dringend
                    </Badge>
                  )}
                  {option.urgency === 'medium' && (
                    <Badge bg="warning" className="position-absolute top-0 end-0 m-2">
                      Wichtig
                    </Badge>
                  )}

                  <div className="menu-icon" style={{ backgroundColor: option.enabled ? option.color : '#6c757d' }}>
                    <FontAwesomeIcon icon={option.icon} size="lg" />
                  </div>

                  <div className="menu-content">
                    <div>
                      <h3 className="menu-title">
                        {option.title}
                        {option.id === 'edit-sick-leave-reports' && incompleteSickLeaveReportsCount > 0 && (
                          <Badge bg="warning" className="ms-2">{incompleteSickLeaveReportsCount}</Badge>
                        )}
                      </h3>
                      <p className="menu-description">{option.description}</p>
                    </div>

                    <div className="menu-action-container">
                      {option.enabled ? (
                        <Button 
                          variant={option.urgency !== 'low' ? (option.urgency === 'high' ? 'danger' : 'warning') : 'outline-primary'}
                          onClick={option.action} 
                          className={`menu-button ${option.urgency !== 'low' ? 'highlight-button' : ''}`}
                          style={{ 
                            backgroundColor: option.urgency !== 'low' ? option.color : 'transparent',
                            borderColor: option.color,
                            color: option.urgency !== 'low' ? 'white' : option.color
                          }}
                        >
                          {option.urgency !== 'low' && option.urgentButtonText ? option.urgentButtonText : option.buttonText}
                        </Button>
                      ) : (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`tooltip-${option.id}`}>
                              {option.disabledReason}
                            </Tooltip>
                          }
                        >
                          <div>
                            <Button 
                              variant="secondary" 
                              disabled 
                              className="menu-button"
                            >
                              Nicht verfügbar
                            </Button>
                          </div>
                        </OverlayTrigger>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </>
      )}

      <Row className="mt-5">
        <Col className="text-center">
          <Button variant="outline-danger" onClick={resetAuth}>
            Abmelden
          </Button>
        </Col>
      </Row>

      {/* Client Info Modal */}
      <ClientDetailModal 
        show={showClientInfoModal}
        onHide={() => setShowClientInfoModal(false)}
        client={currentClient}
        showConfirmButton={false}
        data={data}
      />
    </CustomContainer>
  );
};

export default Dashboard;